// Onglet Dashboard "Sphère" : mêmes réglages que sillage-sphere-tuning.html,
// intégrés directement dans le dashboard (évite d'avoir à naviguer vers une
// page à part). THREE.js et le module de la sphère sont chargés à la
// demande (premier clic sur l'onglet), pas au chargement du dashboard --
// éviter d'alourdir /, /proposer, /galerie, /carte, /shop qui partagent
// tous ce même bundle app.js.

let threeLoadPromise = null;
function loadThreeGlobal() {
  if (window.THREE) return Promise.resolve();
  if (threeLoadPromise) return threeLoadPromise;
  threeLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/assets/vendor/three/three.min.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Échec chargement three.min.js'));
    document.head.appendChild(script);
  });
  return threeLoadPromise;
}

const OUTER_RADIUS = 5; // = SPHERE_RADIUS dans sillageMain.js

// Correspondance id des champs de réglage (préfixe "st" dans index.html) ->
// clé DEFAULT_SPHERE_SETTINGS -- une seule table pour lire/écrire tous les
// réglages "look" d'un coup, comme dans sillage-sphere-tuning.html.
const SETTINGS_FIELDS = {
  particlesX: 'stX', particlesY: 'stY', density: 'stDensity',
  tightness: 'stTightness', fScale: 'stFScale', complexity: 'stComplexity',
  octMult: 'stOctMult', octScale: 'stOctScale', displace: 'stDisplace', flow: 'stFlow',
  pointSize: 'stPointSize', opacity: 'stOpacity', sigmaBase: 'stSigmaBase',
  weightScale: 'stWeightScale', rotSpeed: 'stRotSpeed', tilt: 'stTilt', scale: 'stScale',
  contain: 'stContain', feather: 'stFeather', rimBoost: 'stRimBoost',
};

export function createSphereSettingsDashboardFeature() {
  const canvas = document.getElementById('sphereTuningCanvas');
  const wrap = document.querySelector('.sphere-tuning-wrap');
  const fpsEl = document.getElementById('sphereTuningFps');
  const saveBtn = document.getElementById('sphereTuningSaveBtn');
  const saveStatus = document.getElementById('sphereTuningSaveStatus');

  let loaded = false;
  let sphere = null;
  let scriptOrder = null;

  function readSettingsFromUI() {
    const out = {};
    for (const [key, id] of Object.entries(SETTINGS_FIELDS)) {
      out[key] = parseFloat(document.getElementById(id).value);
    }
    return out;
  }

  function applySettingsToUI(settings) {
    for (const [key, id] of Object.entries(SETTINGS_FIELDS)) {
      const el = document.getElementById(id);
      if (settings[key] !== undefined) {
        el.value = settings[key];
        el.dispatchEvent(new Event('input'));
      }
    }
  }

  function readWeights() {
    return scriptOrder.map((_, i) => parseFloat(document.getElementById('stW' + i).value));
  }

  function applySize(renderer, camera) {
    const w = wrap.clientWidth, h = wrap.clientHeight;
    // Revenu à 2 (comme sillage.html/la page de réglage externe) : plafonner
    // à 1 réduisait bien la charge GPU mais rendait les particules
    // visiblement plus grossières que sur les autres pages -- mauvais
    // compromis, la saccade venait d'ailleurs.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h, false);
    camera.aspect = w / (h || 1);
    camera.updateProjectionMatrix();
  }

  async function load() {
    if (loaded) return;
    loaded = true;

    document.querySelectorAll('.sphere-tuning-controls input[type="range"]').forEach((input) => {
      const span = input.nextElementSibling;
      if (!span || !span.classList || !span.classList.contains('val')) return;
      const update = () => { span.textContent = input.value; };
      input.addEventListener('input', update);
      update();
    });

    try {
      await loadThreeGlobal();
      const [{ createAlphabetSphere, DEFAULT_SPHERE_SETTINGS, SCRIPT_ORDER, computeWeightsFromGenerations }, { fetchGenerations }] =
        await Promise.all([
          import('../sillage/alphabetSphere.js'),
          import('../../services/apiClient.js'),
        ]);
      scriptOrder = SCRIPT_ORDER;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x000000);
      const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
      camera.position.set(0, 0, OUTER_RADIUS * 2.6);
      const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
      applySize(renderer, camera);
      window.addEventListener('resize', () => applySize(renderer, camera));

      let initialSettings = DEFAULT_SPHERE_SETTINGS;
      try {
        const res = await fetch('/api/sphere-settings');
        if (res.ok) initialSettings = await res.json();
      } catch (e) {
        console.warn('[dashboard/sphere] Réglages serveur indisponibles, valeurs par défaut utilisées', e);
      }
      applySettingsToUI(initialSettings);

      sphere = createAlphabetSphere({
        radius: OUTER_RADIUS * 0.95,
        settings: readSettingsFromUI(),
        weights: readWeights(),
      });
      scene.add(sphere.object3D);

      // Vraies punchlines actuelles comme point de départ des curseurs de
      // test (juste pour prévisualiser -- ces curseurs ne sont jamais
      // sauvegardés, seuls les réglages "look" le sont). userEditedWeights
      // évite une course : sans lui, si cette requête répond APRÈS que
      // l'utilisateur a déjà bougé un curseur de poids pour tester, elle
      // écrasait silencieusement son test avec les vrais chiffres (constaté
      // par Rudy -- "je change le curseur, ça ne change pas la couleur",
      // en fait si, puis ça revenait aussitôt sans que ce soit visible).
      let userEditedWeights = false;
      fetchGenerations().then((generations) => {
        if (userEditedWeights) return;
        const weights = computeWeightsFromGenerations(generations);
        SCRIPT_ORDER.forEach((_, i) => {
          const el = document.getElementById('stW' + i);
          el.value = weights[i];
          el.dispatchEvent(new Event('input'));
        });
        sphere.setWeights(weights);
      }).catch(() => {});

      const outerWire = new THREE.Mesh(
        new THREE.SphereGeometry(OUTER_RADIUS, 32, 32),
        new THREE.MeshBasicMaterial({ color: 0x2a3a55, wireframe: true, transparent: true, opacity: 0.15 })
      );
      scene.add(outerWire);

      Object.keys(SETTINGS_FIELDS).forEach((key) => {
        document.getElementById(SETTINGS_FIELDS[key]).addEventListener('input', () => {
          sphere.applySettings(readSettingsFromUI());
        });
      });
      SCRIPT_ORDER.forEach((_, i) => {
        document.getElementById('stW' + i).addEventListener('input', () => {
          userEditedWeights = true;
          sphere.setWeights(readWeights());
        });
      });

      saveBtn.addEventListener('click', async () => {
        saveBtn.disabled = true;
        saveStatus.textContent = 'Enregistrement...';
        try {
          const res = await fetch('/api/sphere-settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(readSettingsFromUI()),
          });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          saveStatus.textContent = 'Enregistré';
        } catch (e) {
          saveStatus.textContent = 'Échec de l\'enregistrement.';
          console.error('[dashboard/sphere] Échec sauvegarde', e);
        } finally {
          saveBtn.disabled = false;
          setTimeout(() => { saveStatus.textContent = ''; }, 5000);
        }
      });

      let lastTs = performance.now();
      let fpsFrames = 0, fpsAccum = 0;
      function animate(ts) {
        requestAnimationFrame(animate);
        // Le canvas reste dans le DOM (juste masqué en CSS, cf.
        // dashboardTabs.js) dès qu'on quitte l'onglet "Sphère" -- sans ce
        // garde-fou, cette boucle continuait à calculer le shader (bruit 5
        // octaves x ~94k particules) et à faire un render() en arrière-plan
        // indéfiniment, même sur un tout autre onglet, ce qui rendait tout
        // le dashboard saccadé (un seul thread JS/GPU partagé).
        if (canvas.offsetParent === null) {
          lastTs = ts;
          return;
        }
        const dt = Math.min(0.05, (ts - lastTs) / 1000);
        lastTs = ts;
        const t = ts / 1000;

        fpsFrames++;
        fpsAccum += dt;
        if (fpsAccum >= 0.5) {
          fpsEl.textContent = (fpsFrames / fpsAccum).toFixed(0) + ' fps';
          fpsFrames = 0; fpsAccum = 0;
        }

        sphere.update(dt, t);
        renderer.render(scene, camera);
      }
      requestAnimationFrame(animate);
    } catch (error) {
      console.error('[dashboard/sphere] Échec d\'initialisation', error);
      wrap.textContent = "Erreur au chargement de la sphère (voir la console).";
    }
  }

  return { load };
}
